import React from "react";
import { Box, Divider, IconButton, Popover, Typography } from "@mui/material";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { LegacyTokens, B13, B13Ink, tint12 } from "../../theme/b13Tokens";

// Batch 6 §6.3(a) — shared colour picker used by prompt-level cards and
// (Batch 7) phase cards. Renders as a large filled rounded-square swatch
// with a centred dark eyedropper glyph. Click opens a Popover with the
// ABA palette + a native <input type="color"> for custom hex.
//
// The swatch itself is the button — no separate label, no hex readout
// (that would compete with the eyedropper). Consumers can pass a size
// override; default 70 matches the reference screenshot.

// Batch 13 Part C — DEFAULT_PALETTE is user-facing swatch data,
// not chrome. Every hex here is what Kerthiga picks from and what
// gets stored on prompt_levels.colour / mastery_workflow_phases.
// colour, so changing values would change already-stored colours
// on next-save round-trips and would render existing rows against
// a different-hex hover state. Legacy tokens preserved deliberately.
const DEFAULT_PALETTE: string[] = [
  LegacyTokens.red,
  LegacyTokens.clay,
  LegacyTokens.amber,
  LegacyTokens.emerald,
  LegacyTokens.teal,
  LegacyTokens.blue,
  LegacyTokens.purple,
  LegacyTokens.brand,
  LegacyTokens.ink3,
];

export type ColorPickerProps = {
  value: string;
  onChange: (hex: string) => void;
  size?: number;
  disabled?: boolean;
  palette?: string[];
  ariaLabel?: string;
};

export default function ColorPicker({
  value,
  onChange,
  size = 70,
  disabled = false,
  palette = DEFAULT_PALETTE,
  ariaLabel = "Choose colour",
}: ColorPickerProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const current = value || LegacyTokens.blue;

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        onClick={(e) => { if (!disabled) setAnchor(e.currentTarget); }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchor(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          backgroundColor: current,
          border: `1px solid ${LegacyTokens.line}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "transform 120ms",
          "&:hover": disabled ? {} : { transform: "scale(1.03)" },
          "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
        }}
      >
        <ColorizeIcon sx={{ color: LegacyTokens.ink, fontSize: size * 0.36 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: `${LegacyTokens.radius.control}px`,
            boxShadow: "0 6px 20px rgba(15, 61, 46, 0.14)",
          },
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 32px)", gap: 1 }}>
          {palette.map((c) => {
            const selected = c.toLowerCase() === current.toLowerCase();
            return (
              <Box
                key={c}
                role="button"
                tabIndex={0}
                aria-label={`Select ${c}`}
                onClick={() => { onChange(c); setAnchor(null); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                  }
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: c,
                  border: `2px solid ${selected ? LegacyTokens.ink : "transparent"}`,
                  boxShadow: selected ? "none" : `inset 0 0 0 1px ${LegacyTokens.line}`,
                  cursor: "pointer",
                  transition: "border-color 120ms",
                  "&:hover": { borderColor: B13Ink.ink2 },
                  "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
                }}
              />
            );
          })}
        </Box>
        <Divider sx={{ my: 1.5, borderColor: LegacyTokens.line }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>Custom:</Typography>
          <input
            type="color"
            value={current}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${LegacyTokens.line}`,
              borderRadius: 4,
              background: "none",
              cursor: "pointer",
            }}
            aria-label="Custom hex colour"
          />
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3, ml: 0.5, fontVariantNumeric: "tabular-nums" }}>
            {current.toUpperCase()}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
